import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../../lib/cn';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Tailwind width utility or a raw value — `w-32`, `w-full`, `w-[8ch]`. */
  readonly width?: string;
  readonly height?: string;
  readonly rounded?: 'chip' | 'control' | 'panel' | 'band';
}

/**
 * A loading block. No shimmer.
 *
 * A shimmer is an animation on a fact that does not exist yet, which is the
 * same category of lie as animating a payroll number. The block is the real
 * row height and the real column width, so the page does not reflow when the
 * data lands — that reflow is the actual cost of a spinner, and it is what
 * this replaces.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { width = 'w-full', height = 'h-4', rounded = 'chip', className, ...rest },
  ref,
) {
  return (
    <div
      {...rest}
      ref={ref}
      aria-hidden="true"
      className={cn(
        'bg-skeleton',
        rounded === 'chip' && 'rounded-chip',
        rounded === 'control' && 'rounded-control',
        rounded === 'panel' && 'rounded-panel',
        rounded === 'band' && 'rounded-band',
        width,
        height,
        className,
      )}
    />
  );
});

export interface SkeletonRowsProps extends HTMLAttributes<HTMLDivElement> {
  /** How many rows to draw. Match what the page usually shows. */
  readonly rows?: number;
  /** One width utility per column, so the skeleton has the grid's shape. */
  readonly columns?: readonly string[];
  /** `row` (52 px) or `compact` (36 px) — the two published densities. */
  readonly density?: 'row' | 'compact';
  /**
   * What is loading, already translated: "Chargement des pointages". Announced
   * once; the blocks themselves are hidden from assistive technology.
   */
  readonly label: string;
}

export const SkeletonRows = forwardRef<HTMLDivElement, SkeletonRowsProps>(function SkeletonRows(
  { rows = 6, columns = ['w-24', 'w-40', 'w-20', 'w-16', 'w-16'], density = 'row', label, className, ...rest },
  ref,
) {
  return (
    <div {...rest} ref={ref} role="status" aria-busy="true" aria-live="polite" className={cn('w-full', className)}>
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className={cn(
            'flex items-center gap-4 border-b border-hairline px-4',
            density === 'row' ? 'h-row' : 'h-row-compact',
          )}
        >
          {columns.map((width, columnIndex) => (
            <Skeleton key={columnIndex} width={width} height="h-3" />
          ))}
        </div>
      ))}
    </div>
  );
});

export interface SkeletonBlockProps extends HTMLAttributes<HTMLDivElement> {
  readonly label: string;
  readonly lines?: number;
}

/** The panel-shaped variant: a title bar and a few lines of body. */
export const SkeletonBlock = forwardRef<HTMLDivElement, SkeletonBlockProps>(
  function SkeletonBlock({ label, lines = 3, className, ...rest }, ref) {
    return (
      <div
        {...rest}
        ref={ref}
        role="status"
        aria-busy="true"
        aria-live="polite"
        className={cn('space-y-3 rounded-panel border border-border bg-surface p-4', className)}
      >
        <span className="sr-only">{label}</span>
        <Skeleton width="w-1/3" height="h-4" />
        {Array.from({ length: lines }, (_, index) => (
          <Skeleton
            key={index}
            width={index === lines - 1 ? 'w-2/3' : 'w-full'}
            height="h-3"
          />
        ))}
      </div>
    );
  },
);
